/**
 * GaugeMeter — radial single-arc gauge for percentage / threshold reads.
 *
 * Sits between the {@link KpiCard} (full headline + delta) and the
 * {@link MetricSparkline} (trend at a glance). The gauge surfaces a
 * single value relative to its domain, with a color scale that lights
 * up green / amber / red depending on whether the value is in the
 * `safe`, `warn`, or `danger` band.
 *
 * Pure SVG so the arc stays sharp at any zoom level and SSR renders
 * without a chart layout phase.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";

export type GaugeBand = "safe" | "warn" | "danger" | "neutral";

export interface GaugeMeterProps {
  /** Current value. */
  readonly value: number;
  /** Minimum value. Defaults to 0. */
  readonly min?: number;
  /** Maximum value. Defaults to 100. */
  readonly max?: number;
  /**
   * Threshold values (in the same units as `min`/`max`) that switch
   * the arc color. Values at or below `thresholds.safe` render as
   * `SAFE`; values above `thresholds.safe` but at or below
   * `thresholds.warn` render as `PRIMARY` (warn band); values above
   * `thresholds.warn` render as `WARN` (danger band).
   */
  readonly thresholds?: { readonly safe: number; readonly warn: number };
  /** Pixel size of the gauge. Defaults to 160. */
  readonly size?: number;
  /** Optional label rendered below the value. */
  readonly label?: string;
  /** Optional value formatter. Defaults to `String(value)`. */
  readonly format?: (value: number) => string;
  /** Accessible label. Defaults to a value-derived string. */
  readonly ariaLabel?: string;
}

function bandFor(value: number, thresholds: { safe: number; warn: number } | undefined): GaugeBand {
  if (thresholds === undefined) return "neutral";
  if (value <= thresholds.safe) return "safe";
  if (value <= thresholds.warn) return "warn";
  return "danger";
}

const BAND_COLOR: Readonly<Record<GaugeBand, string>> = {
  safe: SAFE[500],
  warn: PRIMARY[500],
  danger: WARN[500],
  neutral: PRIMARY[500],
};

/** Renders a radial gauge with a token-aligned threshold scale. */
export const GaugeMeter = React.memo(function GaugeMeter({
  value,
  min = 0,
  max = 100,
  thresholds,
  size = 160,
  label,
  format,
  ariaLabel,
}: GaugeMeterProps): React.JSX.Element {
  const clampedValue = Math.max(min, Math.min(max, value));
  const span = max - min === 0 ? 1 : max - min;
  const fraction = (clampedValue - min) / span;
  const band = bandFor(clampedValue, thresholds);

  // Half-circle gauge: angles run from 180° (left) to 360° (right).
  const centre = size / 2;
  const radius = size / 2 - 12;
  const strokeWidth = 14;
  const startAngle = Math.PI; // 180°
  const endAngle = 2 * Math.PI; // 360°
  const currentAngle = startAngle + (endAngle - startAngle) * fraction;

  const arcPath = (from: number, to: number): string => {
    const x1 = centre + radius * Math.cos(from);
    const y1 = centre + radius * Math.sin(from);
    const x2 = centre + radius * Math.cos(to);
    const y2 = centre + radius * Math.sin(to);
    const largeArc = to - from > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const formatted = format !== undefined ? format(clampedValue) : String(clampedValue);
  const resolvedAriaLabel = ariaLabel ?? `${label ?? "Value"}: ${formatted}`;

  return (
    <svg
      width={size}
      height={size / 2 + 24}
      role="img"
      aria-label={resolvedAriaLabel}
      data-csk-gauge-band={band}
    >
      <path
        d={arcPath(startAngle, endAngle)}
        stroke={GRAY[300]}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
      {fraction > 0 && (
        <path
          d={arcPath(startAngle, currentAngle)}
          stroke={BAND_COLOR[band]}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
        />
      )}
      <text
        x={centre}
        y={centre - 4}
        textAnchor="middle"
        style={{
          fill: GRAY[900],
          fontSize: 22,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatted}
      </text>
      {label !== undefined && (
        <text
          x={centre}
          y={centre + 14}
          textAnchor="middle"
          style={{
            fill: GRAY[600],
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {label}
        </text>
      )}
    </svg>
  );
});
